<template>
  <section id="posts" class="posts-section">
    <div class="container">
      <h2>{{ $t('posts.title') }}</h2>

      <div class="facebook-card">
        <div ref="feedFrame" class="feed-frame">
          <iframe
            v-if="pluginWidth"
            :title="$t('posts.iframeTitle')"
            :src="facebookIframeUrl"
            :width="pluginWidth"
            height="520"
            loading="lazy"
            allow="picture-in-picture"
            referrerpolicy="strict-origin-when-cross-origin"
          ></iframe>
        </div>

        <aside class="brand-rail">
          <span class="brand-accent" aria-hidden="true"></span>
          <img src="@/assets/branding/logo-black.svg" alt="Singing Molenbeek" />
          <a
            href="https://www.facebook.com/singingmolenbeek/"
            target="_blank"
            rel="noopener noreferrer"
          >
            {{ $t('posts.follow') }}
            <span aria-hidden="true">↗</span>
          </a>
        </aside>
      </div>
    </div>
  </section>
</template>

<script setup>
import { computed, onBeforeUnmount, onMounted, ref } from 'vue';

const feedFrame = ref(null);
const pluginWidth = ref(0);
let resizeObserver = null;
let resizeTimer = null;

const facebookIframeUrl = computed(() => (
  'https://www.facebook.com/plugins/page.php'
  + '?href=https%3A%2F%2Fwww.facebook.com%2Fsingingmolenbeek%2F'
  + `&tabs=timeline&width=${pluginWidth.value}&height=520`
  + '&small_header=true&adapt_container_width=true&hide_cover=false&show_facepile=false'
));

const updatePluginWidth = () => {
  if (!feedFrame.value) return;
  const nextWidth = Math.min(500, Math.max(180, Math.round(feedFrame.value.clientWidth)));
  if (nextWidth !== pluginWidth.value) pluginWidth.value = nextWidth;
};

const schedulePluginResize = () => {
  window.clearTimeout(resizeTimer);
  resizeTimer = window.setTimeout(updatePluginWidth, 150);
};

onMounted(() => {
  updatePluginWidth();
  resizeObserver = new ResizeObserver(schedulePluginResize);
  resizeObserver.observe(feedFrame.value);
});

onBeforeUnmount(() => {
  window.clearTimeout(resizeTimer);
  resizeObserver?.disconnect();
});
</script>

<style scoped>
.posts-section {
  padding: clamp(72px, 8vw, 108px) 20px;
  background:
    radial-gradient(circle at 50% 0, rgba(201, 59, 69, 0.055), transparent 42%),
    #fff;
}

.container {
  max-width: 1200px;
  margin: 0 auto;
}

.facebook-card {
  display: grid;
  grid-template-columns: minmax(0, 500px) 150px;
  gap: 14px;
  width: min(100%, 694px);
  margin: 0 auto;
  padding: 12px;
  border: 1px solid var(--border-color);
  border-radius: var(--radius-lg);
  background: #f5f1ed;
  box-shadow: var(--shadow-lg);
}

.feed-frame {
  min-width: 0;
  height: 520px;
  overflow: hidden;
  border: 1px solid #e7e9ec;
  border-radius: 18px;
  background: #fff;
}

.feed-frame iframe {
  display: block;
  width: 100%;
  height: 520px;
  border: 0;
}

.brand-rail {
  position: relative;
  display: flex;
  min-width: 0;
  min-height: 520px;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  border: 1px solid rgba(216, 60, 60, 0.1);
  border-radius: 18px;
  background:
    linear-gradient(155deg, rgba(255, 255, 255, 0.98), rgba(250, 244, 238, 0.95)),
    #fff;
}

.brand-rail img {
  width: 270px;
  max-width: none;
  height: auto;
  transform: rotate(90deg);
  opacity: 0.92;
}

.brand-accent {
  position: absolute;
  top: 28px;
  width: 38px;
  height: 4px;
  border-radius: 999px;
  background: var(--primary-color);
}

.brand-rail a {
  position: absolute;
  right: 12px;
  bottom: 18px;
  left: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 5px;
  padding: 9px 8px;
  border-radius: 999px;
  color: var(--secondary-color);
  background: #fff;
  box-shadow: var(--shadow-sm);
  font-size: 0.78rem;
  font-weight: 700;
  line-height: 1.2;
  text-align: center;
  text-decoration: none;
  transition: color 0.2s ease, transform 0.2s ease, box-shadow 0.2s ease;
}

.brand-rail a:hover,
.brand-rail a:focus-visible {
  color: var(--primary-color);
  box-shadow: var(--shadow-md);
  transform: translateY(-2px);
}

@media (max-width: 720px) {
  .posts-section {
    padding: 60px 10px;
  }

  .facebook-card {
    display: block;
    width: min(100%, 530px);
    padding: 10px;
  }

  .brand-rail {
    display: none;
  }

  .feed-frame,
  .feed-frame iframe {
    height: 520px;
  }
}
</style>
